import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import DOMPurify from 'dompurify';
import Quill from 'quill';

import 'quill/dist/quill.snow.css';

import { useAuth } from '../../contexts/AuthContext';
import { registerReveFormats } from '../helpers/quillFormats';

interface PdfSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const PdfSettingsDialog: React.FC<PdfSettingsDialogProps> = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const { signature: authSignature, updateSignature } = useAuth();
  
  const [signature, setSignature] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);

  // Maximum signature length (500 characters)
  const MAX_SIGNATURE_LENGTH = 500;

  // Initialize Quill when dialog opens
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if(!isOpen) return;
    
    timeoutId = setTimeout(() => {
        // Wait for DOM to be fully rendered before initializing Quill
        if (!editorRef.current || quillRef.current) return;
        
        // Check if there's already a Quill container (from previous session)
        const existingContainer = editorRef.current.querySelector('.ql-container');
        if (existingContainer) {
          existingContainer.remove();
        }
        
        // The <hr> blot lives here: without it Quill drops a rule from the
        // signature, which is what the old <pre> code-block storage worked around.
        registerReveFormats();

        const quill = new Quill(editorRef.current, {
          theme: 'snow',
          modules: {
            toolbar: false, // No toolbar
          },
          placeholder: 'Enter your signature (e.g., name, title, contact information)',
        });

        quillRef.current = quill;

        // Loaded as served. Signatures stored before REV-2259 hold HTML source
        // inside a <pre>, and the backend normalises that on the way out of the
        // column -- every reader of it does, not just this dialog -- so there is
        // nothing to undo here (authentication/signature_html.py).
        if (authSignature) {
          quill.clipboard.dangerouslyPasteHTML(DOMPurify.sanitize(authSignature), 'silent');
        }

        // Listen for text changes
        quill.on('text-change', () => {
          const html = quill.root.innerHTML;
          // Quill returns <p><br></p> for empty content
          const isEmpty = html === '<p><br></p>' || html === '';
          setSignature(isEmpty ? '' : html);
        });
    }, 50);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Always clean up the Quill instance when effect cleanup runs
      // (either when isOpen changes or component unmounts)
      quillRef.current = null;
    };
  }, [isOpen, authSignature, editorRef, quillRef]);

  // Load signature from auth context when dialog opens
  useEffect(() => {
    if (isOpen && authSignature !== undefined) {
      setSignature(authSignature || '');
    }
  }, [isOpen, authSignature]);

  const handleClose = () => {
    // Clean up Quill instance
    if (quillRef.current) {
      quillRef.current = null;
    }
    onClose();
    resetStates();
    // Clear any previous error messages
    setError(null);
  };

  const handleSaveSignature = async () => {
    // Validate signature length
    if (signature.length > MAX_SIGNATURE_LENGTH) {
      setError(`Signature must be ${MAX_SIGNATURE_LENGTH} characters or less.`);
      return;
    }

    setSaving(true);
    // Reset any previous ui states
    setError(null);
    setSuccess(false);

    try {
      // Stored as the plain HTML the editor is showing. It used to be wrapped in
      // a <pre>, which Quill reads back as a code block: the markup came back as
      // escaped text, and re-wrapping it on the next save flattened the
      // formatting for good. The letter writer concatenates this value straight
      // into the letter, so it has to be HTML, not HTML spelled out (REV-2259).
      // updateSignature updates the signature in AuthContext (single source of truth)
      const result = await updateSignature(signature);
      if (result) {
        setSuccess(true);
        setSaving(false);

        // Auto-close after a short delay on success
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        throw new Error('Failed to save signature. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save signature');
      resetStates();
    }
  };

  const resetStates = () => {
    // Clear any previous messages
    setSuccess(false);
    setSaving(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          Signature
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 1 }}>
              Signature saved successfully!
            </Alert>
          )}

          <Box>
            <Box
              sx={{
                height: '150px',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden',
                '& .ql-container.ql-snow': {
                  border: 'none',
                  height: '100%',
                },
                '& .ql-editor': {
                  height: '100%',
                  overflowY: 'auto',
                },
              }}
            >
              <div ref={editorRef} style={{ height: '100%' }} />
            </Box>

            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                }}
              >
                Your signature will be used in newly generated documents and PDF exports.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    signature.length > MAX_SIGNATURE_LENGTH * 0.9
                      ? theme.palette.warning.main
                      : theme.palette.text.secondary,
                  fontSize: '0.75rem',
                }}
              >
                {signature.length}/{MAX_SIGNATURE_LENGTH}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
        >
          Cancel
        </Button>

        <Button
          onClick={handleSaveSignature}
          disabled={saving}
          variant="contained"
        >
          {saving ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: '#fff' }} />
              <Typography variant="body2">Saving...</Typography>
            </Box>
          ) : (
            'Save Signature'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdfSettingsDialog;
